import React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Autocomplete,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add, OpenInNew, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FormRow from './FormRow';
import { getTimezoneLabel } from '../../utils/timezoneUtils';

const BasicInformationTab = ({ formData, handleChange, handleNestedChange, dropdownOptions, displayTimezone, onCreateOperator, onRefreshDropdowns }) => {
  const navigate = useNavigate();
  
  // Filter model variants based on selected manufacturer
  // Note: formData.manufacturer stores the manufacturer NAME (string), not ID
  const filteredModels = React.useMemo(() => {
    if (!formData.manufacturer) {
      return dropdownOptions.MODELS || [];
    }
    return (dropdownOptions.MODELS || []).filter(model => {
      // model.manufacturer can be:
      // 1. An object (populated) with .name property
      // 2. A string (manufacturer name or ID)
      if (typeof model.manufacturer === 'object' && model.manufacturer !== null) {
        // Compare manufacturer name
        return model.manufacturer.name === formData.manufacturer;
      }
      // If it's a string, compare directly (should be manufacturer name)
      return model.manufacturer === formData.manufacturer;
    });
  }, [formData.manufacturer, dropdownOptions.MODELS]);
  
  const handleNavigateToMasterData = (type) => {
    const confirmed = window.confirm(
      `You will be taken to Master Data Management to add a new ${type}.\n\n` +
      `After adding the ${type}, please return to this page - the dropdown will be refreshed automatically.`
    );
    
    if (confirmed) {
      // Determine which tab to open in Master Data Management
      let tabIndex = 0;
      if (type === 'manufacturer') {
        tabIndex = 1; // Manufacturers tab (index 1)
      } else if (type === 'model') {
        tabIndex = 2; // Models tab (index 2)
      }
      
      // Navigate to master data with state to open the correct tab
      navigate('/master-data', { 
        state: { 
          openTab: tabIndex,
          returnTo: '/aircraft',
          refreshDropdowns: true
        } 
      });
    }
  };
  
  const handleManufacturerChange = (e) => {
    handleChange('manufacturer')(e);
    // Clear model when manufacturer changes
    handleChange('model')({ target: { value: '' } });
  };
  return (
    <Box>
      {/* Aircraft Identification Section */}
      <TableContainer 
        component={Paper} 
        variant="outlined" 
        sx={{ 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: 'primary.main', 
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Aircraft Identification</Typography>
        </Box>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(0, 0, 0, 0.08)' } }}>
          <TableBody>
            <FormRow label="Registration Number" required>
              <TextField
                name="registration"
                fullWidth
                size="small"
                value={formData.registration}
                onChange={handleChange('registration', (v) => v.toUpperCase())}
                placeholder="e.g., N123GE"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label="Manufacturer" required>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  name="manufacturer"
                  fullWidth
                  size="small"
                  select
                  value={formData.manufacturer || ''}
                  onChange={handleManufacturerChange}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                >
                  <MenuItem value="">
                    <em>Select a manufacturer</em>
                  </MenuItem>
                  {(dropdownOptions.MANUFACTURER_NAMES || []).map((mn) => (
                    <MenuItem key={mn._id} value={mn.name}>
                      {mn.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip title="Refresh manufacturers list">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (onRefreshDropdowns) {
                        // onRefreshDropdowns accepts a boolean parameter for force refresh
                        if (typeof onRefreshDropdowns === 'function') {
                          onRefreshDropdowns(true);
                        } else {
                          onRefreshDropdowns();
                        }
                      }
                    }}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light', color: 'white' }
                    }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add new manufacturer in Master Data Management">
                  <IconButton
                    size="small"
                    onClick={() => handleNavigateToMasterData('manufacturer')}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light', color: 'white' }
                    }}
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </FormRow>
            <FormRow label="Model Variant" required>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  name="model"
                  fullWidth
                  size="small"
                  select
                  value={formData.model || ''}
                  onChange={handleChange('model')}
                  disabled={!formData.manufacturer}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                >
                  <MenuItem value="">
                    <em>{formData.manufacturer ? 'Select a model variant' : 'Select manufacturer first'}</em>
                  </MenuItem>
                  {filteredModels.map((model) => (
                    <MenuItem key={model._id} value={model.name}>
                      {model.name} {model.type ? `(${model.type})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip title="Refresh model variants list">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (onRefreshDropdowns) {
                        // onRefreshDropdowns accepts a boolean parameter for force refresh
                        if (typeof onRefreshDropdowns === 'function') {
                          onRefreshDropdowns(true);
                        } else {
                          onRefreshDropdowns();
                        }
                      }
                    }}
                    disabled={!formData.manufacturer}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light', color: 'white' },
                      '&.Mui-disabled': { opacity: 0.5 }
                    }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add new model variant in Master Data Management">
                  <IconButton
                    size="small"
                    onClick={() => handleNavigateToMasterData('model')}
                    disabled={!formData.manufacturer}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light', color: 'white' },
                      '&.Mui-disabled': { opacity: 0.5 }
                    }}
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </FormRow>
            <FormRow label="Serial Number" required>
              <TextField
                name="serialNumber"
                fullWidth
                size="small"
                value={formData.serialNumber}
                onChange={handleChange('serialNumber')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label={displayTimezone ? `Manufacturing Date (${getTimezoneLabel(displayTimezone)})` : 'Manufacturing Date'}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.dateOfManufacture}
                onChange={handleChange('dateOfManufacture')}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label="Aircraft Type" required>
              <TextField
                fullWidth
                size="small"
                select
                value={formData.aircraftType}
                onChange={handleChange('aircraftType')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                <MenuItem value="Fixed Wing">Fixed Wing</MenuItem>
                <MenuItem value="Helicopter">Helicopter</MenuItem>
              </TextField>
            </FormRow>
            <FormRow label="Max Passengers" required>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={formData.maxPassengers}
                onChange={handleChange('maxPassengers')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label="ATA Chapter">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.ataChapterId || ''}
                onChange={handleChange('ataChapterId')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                <MenuItem value="">None</MenuItem>
                {(dropdownOptions.ATA_CHAPTERS || []).map((chapter) => (
                  <MenuItem key={chapter._id} value={chapter._id}>
                    {chapter.code} - {chapter.name}
                  </MenuItem>
                ))}
              </TextField>
            </FormRow>
            <FormRow label="Approved Technical Log Book Format">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.approvedTechnicalLogBookFormatId || ''}
                onChange={handleChange('approvedTechnicalLogBookFormatId')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                helperText="Affects only new Technical Log Book entries; existing entries keep their recorded format."
              >
                <MenuItem value="">Use system default</MenuItem>
                {(dropdownOptions.TECHNICAL_LOG_BOOK_FORMATS || []).map((fmt) => (
                  <MenuItem key={fmt._id} value={fmt._id}>
                    {fmt.version}
                  </MenuItem>
                ))}
              </TextField>
            </FormRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Classification & Ownership Section */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: 'primary.main', 
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Classification & Ownership</Typography>
        </Box>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(0, 0, 0, 0.08)' } }}>
          <TableBody>
            <FormRow label="Category">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.category}
                onChange={handleChange('category')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                {(dropdownOptions.CATEGORY || []).map((option) => (
                  <MenuItem key={option._id} value={option.value}>
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>
            </FormRow>
            <FormRow label="Hour Type">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.hourType}
                onChange={handleChange('hourType')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                {(dropdownOptions.HOUR_TYPE || []).map((option) => (
                  <MenuItem key={option._id} value={option.value}>
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>
            </FormRow>
            <FormRow label="Owner">
              <TextField
                fullWidth
                size="small"
                value={formData.owner}
                onChange={handleChange('owner')}
                placeholder="Who owns this aircraft (optional)"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label="Operator/Lessee">
              <Autocomplete
                freeSolo
                options={dropdownOptions.OPERATOR || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.value || '';
                }}
                value={formData.operator || ''}
                onChange={(event, newValue) => {
                  const value = typeof newValue === 'string' ? newValue : (newValue?.value || '');
                  handleChange('operator')({ target: { value } });
                }}
                onInputChange={async (event, newInputValue, reason) => {
                  if (reason === 'input' && newInputValue) {
                    // Update the form value as user types
                    handleChange('operator')({ target: { value: newInputValue } });
                  }
                }}
                onBlur={async (event) => {
                  // Get the current input value from the formData
                  const inputValue = formData.operator?.trim();
                  if (inputValue && onCreateOperator) {
                    // Check if the value already exists in the options
                    const exists = (dropdownOptions.OPERATOR || []).some(
                      opt => opt.value?.toLowerCase() === inputValue.toLowerCase()
                    );
                    if (!exists) {
                      // Create new operator option and add to master dropdown
                      try {
                        await onCreateOperator(inputValue);
                      } catch (error) {
                        console.error('Error creating operator:', error);
                        // Don't show error to user - the value will still be saved to the aircraft record
                        // Only show error if it's a permission issue
                        if (error.response?.status === 403) {
                          // Silently fail - user doesn't have permission to add to master dropdown
                          // but the value will still be saved to the aircraft
                        }
                      }
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="Type to search or add new operator/lessee"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id || option}>
                    {typeof option === 'string' ? option : option.value}
                  </li>
                )}
              />
            </FormRow>
            <FormRow label="Maintenance Service Program Provider" fullWidth>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                value={formData.maintenanceServiceProgramProvider}
                onChange={handleChange('maintenanceServiceProgramProvider')}
                placeholder="Usually the address of the provider"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Location & Status Section */}
      <TableContainer 
        component={Paper} 
        variant="outlined" 
        sx={{ 
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: 'primary.main', 
            color: 'white',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Location & Status</Typography>
        </Box>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(0, 0, 0, 0.08)' } }}>
          <TableBody>
            <FormRow label="Current Airport (ICAO Code)">
              <TextField
                fullWidth
                size="small"
                value={formData.currentLocation.airport}
                onChange={handleNestedChange('currentLocation', 'airport', (v) => v.toUpperCase())}
                placeholder="e.g., VOBL, VABB"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label="Hangar">
              <TextField
                fullWidth
                size="small"
                value={formData.currentLocation.hangar}
                onChange={handleNestedChange('currentLocation', 'hangar')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
            <FormRow label="Status">
              <TextField
                fullWidth
                size="small"
                select
                value={formData.status}
                onChange={handleChange('status')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              >
                <MenuItem value="AVAILABLE">Available</MenuItem>
                <MenuItem value="IN_FLIGHT">In Flight</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                <MenuItem value="INSPECTION">Inspection</MenuItem>
                <MenuItem value="GROUNDED">Grounded</MenuItem>
              </TextField>
            </FormRow>
            <FormRow label="Remarks" fullWidth>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={formData.remarks}
                onChange={handleChange('remarks')}
                placeholder="Add any remarks or notes about this aircraft..."
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
              />
            </FormRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BasicInformationTab;

